import { useState, useEffect, useRef } from 'react';
import { userPreferences } from '../utils/userPreferences';

interface UseLocationReturn {
  userLocation: [number, number] | null;
  isTrackingLocation: boolean;
  locationPermission: 'prompt' | 'granted' | 'denied';
  toggleLocationTracking: () => Promise<void>;
}

const ODENSE_CENTRUM: [number, number] = [55.40093586186357, 10.390809460934186];

/**
 * Custom hook for managing user location tracking
 * Handles geolocation permissions, continuous tracking, and persistence
 */
export function useLocation(): UseLocationReturn {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(
    userPreferences.lastKnownLocation
  );
  const [isTrackingLocation, setIsTrackingLocation] = useState(
    userPreferences.isLocationTrackingEnabled
  );
  const [locationPermission, setLocationPermission] = useState<'prompt' | 'granted' | 'denied'>('prompt');

  const watchIdRef = useRef<number | null>(null);
  const lastKnownLocationRef = useRef<[number, number] | null>(null);
  const locationRequestedRef = useRef(false);

  // Start continuous location tracking
  const startLocationTracking = () => {
    if (watchIdRef.current !== null) {
      return; // Already tracking
    }

    setIsTrackingLocation(true);

    const options: PositionOptions = {
      enableHighAccuracy: true, // Use GPS for more frequent updates
      timeout: 10000,
      maximumAge: 0, // Always get fresh location, no caching
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const newLocation: [number, number] = [position.coords.latitude, position.coords.longitude];
        // Store as last known location
        lastKnownLocationRef.current = newLocation;
        setUserLocation(newLocation);

        // Persist to preferences for future sessions
        userPreferences.setLastKnownLocation(newLocation);

        console.log('📍 [Location] Updated to:', {
          lat: newLocation[0],
          lng: newLocation[1],
          accuracy: position.coords.accuracy,
          timestamp: new Date().toLocaleTimeString(),
        });
      },
      (error) => {
        console.error('Location tracking error:', error);

        // Use last known location if available, otherwise fall back to default
        const fallbackLocation = lastKnownLocationRef.current || ODENSE_CENTRUM;

        if (lastKnownLocationRef.current) {
          console.log('📍 [Location] Using last known location due to error:', {
            lat: fallbackLocation[0],
            lng: fallbackLocation[1],
            reason: 'location_update_failed',
          });
        } else {
          console.log('📍 [Location] Using default location (Odense centrum):', {
            lat: fallbackLocation[0],
            lng: fallbackLocation[1],
            reason: 'no_previous_location',
          });
        }

        // Only update if we don't have any location yet
        if (!userLocation) {
          setUserLocation(fallbackLocation);
        }
      },
      options
    );
  };

  // Stop location tracking
  const stopLocationTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsTrackingLocation(false);
    }
  };

  // Toggle location tracking manually
  const toggleLocationTracking = async () => {
    if (isTrackingLocation) {
      stopLocationTracking();
      userPreferences.setLocationTrackingEnabled(false);
      console.log('📍 [Location] Tracking disabled by user');
    } else {
      if (locationPermission === 'denied') {
        // Show instruction to user to enable in browser settings
        alert('Location access is denied. Please enable location access in your browser settings and refresh the page.');
        return;
      }

      if (locationPermission === 'prompt' || locationPermission === 'granted') {
        startLocationTracking();
        userPreferences.setLocationTrackingEnabled(true);
        console.log('📍 [Location] Tracking enabled by user');
      }
    }
  };

  // Initialize location tracking
  useEffect(() => {
    // Prevent multiple location requests in StrictMode
    if (locationRequestedRef.current) {
      return;
    }
    locationRequestedRef.current = true;

    const initializeLocation = async () => {
      // Load last known location from preferences
      const storedLocation = userPreferences.lastKnownLocation;
      if (storedLocation) {
        lastKnownLocationRef.current = storedLocation;
        setUserLocation(storedLocation);
        console.log('📍 [Location] Loaded from preferences:', {
          lat: storedLocation[0],
          lng: storedLocation[1],
          source: 'userPreferences',
        });
      }

      if (!navigator.geolocation) {
        const fallbackLocation = lastKnownLocationRef.current || ODENSE_CENTRUM;
        setUserLocation(fallbackLocation);
        setLocationPermission('denied');
        return;
      }

      try {
        // Check permission status
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermission(permission.state as 'prompt' | 'granted' | 'denied');

        if (permission.state === 'denied') {
          const fallbackLocation = lastKnownLocationRef.current || ODENSE_CENTRUM;
          setUserLocation(fallbackLocation);
          return;
        }

        // Start location tracking only if user preference allows it
        if (userPreferences.isLocationTrackingEnabled) {
          startLocationTracking();
        } else {
          console.log('📍 [Location] Tracking disabled by user preference');
        }

        // Listen for permission changes
        permission.addEventListener('change', () => {
          setLocationPermission(permission.state as 'prompt' | 'granted' | 'denied');
          if (permission.state === 'granted' && userPreferences.isLocationTrackingEnabled) {
            startLocationTracking();
          } else if (permission.state === 'denied') {
            stopLocationTracking();
            const fallbackLocation = lastKnownLocationRef.current || ODENSE_CENTRUM;
            setUserLocation(fallbackLocation);
          }
        });
      } catch (error) {
        console.error('Error checking location permission:', error);
        const fallbackLocation = lastKnownLocationRef.current || ODENSE_CENTRUM;
        setUserLocation(fallbackLocation);
        setLocationPermission('denied');
      }
    };

    initializeLocation();

    // Cleanup on unmount
    return () => {
      stopLocationTracking();
    };
  }, []);

  return {
    userLocation,
    isTrackingLocation,
    locationPermission,
    toggleLocationTracking,
  };
}
